import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import {Box, CircularProgress} from '@mui/material';
import {BotInfo, BrowserInfo, detect, NodeInfo, ReactNativeInfo, SearchBotDeviceInfo} from 'detect-browser';
import {CustomContentProps, useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SnackbarContent as CustomSnackbarContent} from './SnackbarContent';
import {useExtensionConnection} from '../../hooks/extension/ExtensionProvider';

interface EstablishingConnectionProps extends CustomContentProps {}

export const EstablishingConnection = React.forwardRef<HTMLDivElement, EstablishingConnectionProps>(({id}, ref) => {
  const {closeSnackbar} = useSnackbar();
  const {t} = useTranslation();

  const {isSearching: isExtensionSearching, isExtensionFound, isOutdated, version} = useExtensionConnection();

  const [browser, setBrowser] = useState<
    BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo | undefined
  >();

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  useEffect(() => {
    const browser = detect();
    setBrowser(browser ?? undefined);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isExtensionFound) {
      timer = setTimeout(() => handleDismiss(), 5000);
    }
    return () => {
      timer && clearTimeout(timer);
    };
  }, [isExtensionFound]);

  return isExtensionSearching ? (
    <CustomSnackbarContent
      ref={ref}
      color="info"
      icon={<CircularProgress color="inherit" size={20} />}
      title={t('extension.establishingConnection')}
      content={t('extension.establishingConnectionContent')}
      onClose={handleDismiss}
      bgWhite
      titleColor="info"
      dividerColor={false}
    />
  ) : version === undefined ? (
    <CustomSnackbarContent
      ref={ref}
      color="error"
      icon={<WarningIcon />}
      title={t(`extension.notfoundtitle`)}
      content={
        // Le lien « cette page » arrive brut de la traduction : sans couleur du thème, il gardait le bleu
        // du navigateur, illisible en sombre.
        <Box component="span" sx={{'& a': {color: 'primary.main'}}}>
          {browser?.name === 'firefox' ? (
            <span dangerouslySetInnerHTML={{__html: t(`extension.notfound.firefox`)}} />
          ) : browser?.name === 'chrome' ? (
            <span dangerouslySetInnerHTML={{__html: t(`extension.notfound.chrome`)}} />
          ) : browser?.name === 'edge-chromium' ? (
            <span dangerouslySetInnerHTML={{__html: t(`extension.notfound.edge`)}} />
          ) : (
            <span dangerouslySetInnerHTML={{__html: t(`extension.notfound.unknow`)}} />
          )}
        </Box>
      }
      onClose={handleDismiss}
      bgWhite
      titleColor="info"
      dividerColor={false}
    />
  ) : isOutdated ? (
    <CustomSnackbarContent
      ref={ref}
      color="warning"
      icon={<WarningIcon />}
      title={t('extension.extensionOutdated')}
      content={t('extension.extensionOutdatedContent', {
        version,
      })}
      onClose={handleDismiss}
      bgWhite
      titleColor="info"
      dividerColor={false}
    />
  ) : (
    <CustomSnackbarContent
      ref={ref}
      color="info"
      icon={<VerifiedIcon />}
      title={t('extension.extensionInfo')}
      content={t('extension.extensionInfoContent', {version})}
      onClose={handleDismiss}
      bgWhite
      titleColor="info"
      dividerColor={false}
    />
  );
});
